import { useCallback, useEffect, useRef } from 'react';
import { authClient } from '../lib/authClient.js';

/**
 * Suit la session Better Auth et ouvre silencieusement une session anonyme à la première
 * visite. Expose les actions de connexion / inscription / déconnexion. L'inscription depuis
 * une session anonyme la convertit en compte réel : c'est le plugin `anonymous` qui s'en
 * charge côté serveur, en transférant le codex au passage.
 */
export function useUser() {
  const { data: session, isPending } = authClient.useSession();
  const user = session?.user ?? null;
  const sessionAnonymeTentee = useRef(false);

  // Personne n'arrive avec un compte, et le jeu enregistre la progression dès le premier écran :
  // il faut donc une identité avant même que le joueur ait songé à s'inscrire.
  useEffect(() => {
    if (isPending || user || sessionAnonymeTentee.current) return;
    sessionAnonymeTentee.current = true;

    authClient.signIn.anonymous().then(({ error }) => {
      if (error) {
        // eslint-disable-next-line no-console
        console.warn('[useUser] session anonyme impossible :', error.message);
      }
    });
  }, [isPending, user]);

  const isAnonymous = user?.isAnonymous === true;
  const isAuthenticated = !!user && !isAnonymous;

  const signIn = useCallback(async (email, password) => {
    const { error } = await authClient.signIn.email({ email, password });
    return { error: error ? messageErreur(error) : null };
  }, []);

  const signUp = useCallback(
    async (email, password, metadata = {}) => {
      // Lu avant l'appel : la session aura changé quand il rendra la main.
      const upgrading = isAnonymous;
      const { error } = await authClient.signUp.email({
        email,
        password,
        // `name` est obligatoire en base ; l'AuthModal le demande, mais pas au point d'en
        // faire une condition pour jouer.
        name: metadata.name?.trim() || email.split('@')[0],
      });

      if (error) return { error: messageErreur(error), upgrading };
      // Aucun SMTP dans l'infrastructure : la session est ouverte tout de suite, sans passer
      // par une boîte de réception.
      return { error: null, upgrading };
    },
    [isAnonymous]
  );

  const signInWithGoogle = useCallback(async () => {
    const { error } = await authClient.signIn.social({ provider: 'google', callbackURL: '/' });
    return { error: error ? messageErreur(error) : null };
  }, []);

  /** Déconnecte, puis laisse l'effet ci-dessus rouvrir une session anonyme : on ne renvoie
   *  jamais le joueur devant un jeu qui ne sait plus qui il est. */
  const signOut = useCallback(async () => {
    await authClient.signOut();
    sessionAnonymeTentee.current = false;
  }, []);

  return {
    user,
    loading: isPending,
    isAnonymous,
    isAuthenticated,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
  };
}

// ---------------------------------------------------------------------------

const MESSAGES = {
  INVALID_EMAIL_OR_PASSWORD: 'Email ou mot de passe incorrect.',
  USER_NOT_FOUND: 'Email ou mot de passe incorrect.',
  USER_ALREADY_EXISTS: 'Un compte existe déjà avec cet email.',
  PASSWORD_TOO_SHORT: 'Mot de passe trop court (8 caractères minimum).',
  PASSWORD_TOO_LONG: 'Mot de passe trop long.',
  INVALID_EMAIL: 'Email invalide.',
  EMAIL_NOT_VERIFIED: 'Email non confirmé.',
};

function messageErreur(error) {
  if (MESSAGES[error?.code]) return MESSAGES[error.code];
  if (error?.status === 429) return 'Trop de tentatives. Attends une minute.';
  return error?.message || 'Une erreur est survenue.';
}
